import React from "react"
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap"
import { Link } from "react-router-dom"
import moment from "moment"
import { capitalize } from "lodash"

import defaultProfilePicture from "../../../assets/images/users/default-profile-picture.png"
import OsImpersonateAction from "../OsImpersonateAction"
import { getClubProfileImage } from "./ClubProfile/profileDisplay"
import { TranslationMarkers } from "components/Common/TranslationMarker"
import "assets/scss/custom/components/_directoryActions.scss"

// The club's text blocks (ADR 0013) its row marks.
const CLUB_TEXT_MARKS = [["about", "About the club"], ["directions", "Directions"]]

export const formatClubLocation = club => {
  const compactLocation = [club?.location?.city, club?.location?.country]
    .filter(Boolean)
    .join(", ")

  return compactLocation || club?.location?.name || "Location not set"
}

export const getClubCourtCount = club => {
  const currentCourtCount = Array.isArray(club?.courts)
    ? club.courts.length
    : null

  if (currentCourtCount > 0) return currentCourtCount

  const sports = club?.sports?.length ? club.sports : Object.keys(club?.courtsInfo || {})
  const counts = sports.map(sport => club?.courtsInfo?.[sport]?.totalCourts)
  return counts.length && counts.every(count => Number.isInteger(count) && count >= 0)
    ? counts.reduce((total, count) => total + count, 0) : null
}

const statusClass = status => {
  if (status === "activated") return "success"
  if (status === "pending") return "info"
  if (status === "blocked") return "warning"
  return "danger"
}

const ClubIdentity = ({ club, listState }) => (
  <div className="club-identity-cell">
    <img
      src={getClubProfileImage(club)}
      alt=""
      className="club-list-avatar"
      loading="lazy"
      onError={event => {
        event.currentTarget.onerror = null
        event.currentTarget.src = defaultProfilePicture
      }}
    />
    <div className="min-w-0">
      <Link
        to={{ pathname: `/accounts/clubs/${club.id}`, state: listState }}
        className="club-list-name text-dark"
      >
        {club.clubName || club.businessName || "Unnamed club"}
      </Link>
      <div className="club-list-meta">
        <span>#{club.clubId}</span>
        <span title={formatClubLocation(club)}>{formatClubLocation(club)}</span>
      </div>
      <TranslationMarkers attention={club.translationAttention} blocks={CLUB_TEXT_MARKS} />
    </div>
  </div>
)

const ClubOwner = ({ club }) => {
  const ownerName = [club?.ownerInfo?.firstName, club?.ownerInfo?.lastName]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="club-owner-cell">
      <span className={ownerName ? "club-owner-name" : "text-muted"}>
        {ownerName || "Owner not set"}
      </span>
      {club?.ownerInfo?.email ? (
        <a href={`mailto:${club.ownerInfo.email}`} title={club.ownerInfo.email}>
          <i className="bx bx-envelope" aria-hidden="true" />
          <span>{club.ownerInfo.email}</span>
        </a>
      ) : (
        <span className="text-muted font-size-12">Email not set</span>
      )}
      {club?.ownerInfo?.phone ? (
        <a href={`tel:${club.ownerInfo.phone}`}>
          <i className="bx bx-phone" aria-hidden="true" />
          <span>{club.ownerInfo.phone}</span>
        </a>
      ) : (
        <span className="text-muted font-size-12">Phone not set</span>
      )}
    </div>
  )
}

const ClubServices = ({ club }) => {
  const sports = Array.isArray(club?.sports) ? club.sports : []
  const courtCount = getClubCourtCount(club)
  const bookingEnabled = Boolean(club?.bookingSettings?.bookingEnabled)

  return (
    <div className="club-services-cell">
      <div className="club-sport-list">
        {sports.length ? (
          sports.map(sport => (
            <span className="badge badge-soft-primary" key={sport}>
              {capitalize(sport)}
            </span>
          ))
        ) : (
          <span className="text-muted">Sports not set</span>
        )}
      </div>
      <span className="text-muted font-size-12">
        {courtCount === null
          ? "Courts not provided"
          : `${courtCount} ${courtCount === 1 ? "court" : "courts"}`}
      </span>
      <span
        className={`club-booking-state ${
          bookingEnabled ? "is-enabled" : "is-disabled"
        }`}
      >
        <i className="mdi mdi-circle" aria-hidden="true" />
        Booking {bookingEnabled ? "on" : "off"}
      </span>
    </div>
  )
}

const ClubAccount = ({ club }) => (
  <div className="club-account-cell">
    <div>
      <span className={`badge badge-${statusClass(club?.status)}`}>
        {capitalize(club?.status || "unknown")}
      </span>
    </div>
    <span className="text-muted font-size-12">
      Joined{" "}
      {club?.createdAt ? moment(club.createdAt).format("D MMM YYYY") : "—"}
    </span>
  </div>
)

const ClubActions = ({ club, listState }) => (
  <div className="club-actions-cell directory-row-actions">
    <Link
      to={{ pathname: `/accounts/clubs/${club.id}`, state: listState }}
      className="btn btn-outline-primary btn-sm directory-action-button"
      aria-label={`Open club: ${club.clubName || "Unnamed club"}`}
    >
      Open
    </Link>
    <UncontrolledDropdown>
      <DropdownToggle
        tag="button"
        type="button"
        className="btn btn-light btn-sm directory-action-button"
        aria-label={`More actions for ${club.clubName || "club"}`}
      >
        <i className="mdi mdi-dots-horizontal" aria-hidden="true" />
      </DropdownToggle>
      <DropdownMenu right>
        <OsImpersonateAction account={{ id: club.ownerId, email: club.ownerInfo?.email }} clubOwner dropdown />
        <DropdownItem tag={Link} to={{ pathname: `/accounts/clubs/${club.id}/edit`, state: listState }}>
          Edit club
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  </div>
)

const listColumns = listState => [
  {
    dataField: "clubId",
    text: "Club",
    sort: true,
    headerStyle: { width: "28%" },
    formatter: (cellContent, club) => <ClubIdentity club={club} listState={listState} />,
  },
  {
    dataField: "ownerInfo.email",
    text: "Owner",
    headerStyle: { width: "25%" },
    formatter: (cellContent, club) => <ClubOwner club={club} />,
  },
  {
    dataField: "sports",
    text: "Services",
    headerStyle: { width: "20%" },
    formatter: (cellContent, club) => <ClubServices club={club} />,
  },
  {
    dataField: "createdAt",
    text: "Account",
    sort: true,
    headerStyle: { width: "15%" },
    formatter: (cellContent, club) => <ClubAccount club={club} />,
  },
  {
    dataField: "menu",
    isDummyField: true,
    text: "Actions",
    headerAlign: "right",
    align: "right",
    headerStyle: { width: "12%" },
    formatter: (cellContent, club) => <ClubActions club={club} listState={listState} />,
  },
]

export default listColumns
